/**
 * Media Upload and Display Configuration
 * Centralized settings for all media-related functionality
 */

export const mediaConfig = {
  // Supported file formats
  formats: {
    image: {
      extensions: ['jpg', 'jpeg', 'png', 'heic', 'heif', 'avif', 'webp', 'mpo'],
      mimeTypes: [
        'image/jpeg',
        'image/jpg', 
        'image/png',
        'image/heic',
        'image/heif',
        'image/avif',
        'image/webp'
      ]
    },
    video: {
      extensions: ['mp4', 'webm', 'mov'],
      mimeTypes: [
        'video/mp4',
        'video/webm',
        'video/quicktime'
      ]
    },
    audio: {
      extensions: ['mp3', 'wav'],
      mimeTypes: [
        'audio/mpeg',
        'audio/mp3',
        'audio/wav',
        'audio/wave',
        'audio/x-wav'
      ]
    }
  },

  // File size limits (in bytes)
  maxSize: {
    image: 10 * 1024 * 1024,  // 10MB
    video: 500 * 1024 * 1024, // 500MB
    audio: 50 * 1024 * 1024,  // 50MB
    total: 1024 * 1024 * 1024  // 1GB total per upload session
  },

  // Upload settings
  upload: {
    maxFiles: 20,              // Maximum files per upload session
    chunkSize: 5 * 1024 * 1024, // 5MB chunks for large files
    timeout: 300000,           // 5 minutes timeout
    maxRetries: 3,             // Number of retry attempts
    retryDelay: 1000           // Delay between retries in ms
  },

  // Display settings
  display: {
    thumbnailSize: {
      width: 200,
      height: 200
    },
    previewSize: {
      width: 800,
      height: 600
    },
    lazyLoadOffset: '50px',   // Intersection observer margin
    maxPreviewItems: {
      list: 4,                 // Max items in list view
      grid: 12                 // Max items in grid view
    }
  },

  // Cache settings
  cache: {
    memoryCache: {
      maxSize: 50 * 1024 * 1024,  // 50MB memory cache
      ttl: 3600000                 // 1 hour TTL
    },
    indexedDB: {
      dbName: 'MediaCache',
      dbVersion: 1,
      storeName: 'media',
      maxSize: 500 * 1024 * 1024  // 500MB IndexedDB cache
    }
  },

  // Audio visualization settings
  audio: {
    waveform: {
      fftSize: 256,
      smoothingTimeConstant: 0.8,
      minDecibels: -90,
      maxDecibels: -10
    }
  },

  // Video settings
  video: {
    preload: 'metadata',       // 'none', 'metadata', 'auto'
    defaultMuted: true,        // Start muted for autoplay compatibility
    thumbnailTime: 1.0         // Extract thumbnail at 1 second
  },

  // Validation messages
  messages: {
    unsupportedFormat: 'File format not supported. Please use: ',
    fileTooLarge: 'File is too large. Maximum size is: ',
    uploadFailed: 'Failed to upload file. Please try again.',
    uploadSuccess: 'File uploaded successfully.',
    tooManyFiles: `Maximum number of files exceeded. Limit is: `
  }
}

// Helper functions
export const getAcceptedFormats = (type = null) => {
  if (!type) {
    // Return all accepted formats
    const formats = []
    Object.values(mediaConfig.formats).forEach(format => {
      formats.push(...format.extensions.map(ext => `.${ext}`))
      formats.push(...format.mimeTypes)
    })
    return formats.join(',')
  }
  
  const format = mediaConfig.formats[type]
  if (!format) return ''
  
  const extensions = format.extensions.map(ext => `.${ext}`)
  return [...extensions, ...format.mimeTypes].join(',')
}

export const getFileType = (file) => {
  const extension = file.name.split('.').pop().toLowerCase()
  const mimeType = file.type.toLowerCase()
  
  for (const [type, format] of Object.entries(mediaConfig.formats)) {
    if (format.extensions.includes(extension) || 
        format.mimeTypes.includes(mimeType)) {
      return type
    }
  }
  
  return null
}

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

export const validateFile = (file) => {
  const fileType = getFileType(file)
  
  if (!fileType) {
    return {
      valid: false,
      error: `${mediaConfig.messages.unsupportedFormat} ${Object.values(mediaConfig.formats).flatMap(f => f.extensions).join(', ')}`
    }
  }
  
  const maxSize = mediaConfig.maxSize[fileType]
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `${mediaConfig.messages.fileTooLarge} ${formatFileSize(maxSize)}`
    }
  }
  
  return { valid: true, type: fileType }
}

export default mediaConfig